import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectMatterById } from '@/matter/store/selectors'
import { matterPaths } from '@/routing/paths'
import { FileClock, MedChron, MedicalBillsNew } from '@/assets/icons'
import { canViewTimeline, canViewMedChron, canViewMedicalBills } from '@/matter/utils/matter-permissions'
import { selectTimelineAssetError, selectTimelineAssetForMatter } from '@/timeline/asset/store/selectors'
import { selectMedChronAssetError, selectMedChronAssetForMatter } from '@/medical-chronology/asset/store/selectors'
import { selectMedSummaryAssetError, selectMedSummaryAssetForMatter } from '@/medical-billing-summaries/asset/store/selectors'
import { TimelineAssetActions } from '@/timeline/asset/store/slice'
import { MedChronAssetActions } from '@/medical-chronology/asset/store/slice'
import { MedSummaryAssetActions } from '@/medical-billing-summaries/asset/store/slice'
import { TimelineAssetTabs } from '@/timeline/asset/constants'
import { MedChronAssetTabs } from '@/medical-chronology/asset/constants'
import { MedSummaryAssetTabs } from '@/medical-billing-summaries/asset/constants'
import type { SideNavItemConfig } from '../SideNav'

type MatterRecordsItems = {
  items: SideNavItemConfig[]
  showBuildButton: boolean
}

/**
 * Returns the nav items and build-button state for the Matter Records section.
 *
 * An item appears only when its asset exists (truthy selector result) and the viewer
 * has permission to see that record type. `showBuildButton` is true when at least one
 * viewable record type has not been created yet.
 */
export function useMatterRecordsItems(matterId: string): MatterRecordsItems {
  const dispatch = useAppDispatch()
  const matter = useAppSelector((state) => selectMatterById(state, { matterId }))
  const timelineAsset = useAppSelector((state) => selectTimelineAssetForMatter(state, { matterId }))
  const medChronAsset = useAppSelector((state) => selectMedChronAssetForMatter(state, { matterId }))
  const medSummaryAsset = useAppSelector((state) => selectMedSummaryAssetForMatter(state, { matterId }))
  const timelineAssetError = useAppSelector((state) => selectTimelineAssetError(state, { matterId }))
  const medChronAssetError = useAppSelector((state) => selectMedChronAssetError(state, { matterId }))
  const medSummaryAssetError = useAppSelector((state) => selectMedSummaryAssetError(state, { matterId }))

  // Load asset overviews for this matter so the sidebar knows which record types exist.
  // The listeners skip another overview-only request while one is in flight, but let a
  // page's full request run so it can load entries or events and files.
  useEffect(() => {
    dispatch(TimelineAssetActions.fetchAssetForMatter({ matterId, overviewOnly: true }))
    dispatch(MedChronAssetActions.fetchAssetForMatter({ matterId, overviewOnly: true }))
    dispatch(MedSummaryAssetActions.fetchAssetForMatter({ matterId, overviewOnly: true }))
  }, [matterId, dispatch])

  // Hold the list until every fetch has settled.
  // `undefined` = in-flight; `null` = no asset; object = exists. A fetch error also counts as
  // settled so that a transient failure doesn't permanently blank the section.
  const timelineSettled = timelineAsset !== undefined || timelineAssetError
  const medChronSettled = medChronAsset !== undefined || medChronAssetError
  const medSummarySettled = medSummaryAsset !== undefined || medSummaryAssetError
  if (!timelineSettled || !medChronSettled || !medSummarySettled) return { items: [], showBuildButton: false }

  const items: SideNavItemConfig[] = [
    ...(canViewTimeline(matter) && timelineAsset
      ? [{ id: 'timeline', label: 'Timeline', href: matterPaths.timeline.tab(matterId, TimelineAssetTabs.ALL_EVENTS), icon: FileClock, showBetaTag: true }]
      : []),
    ...(canViewMedChron(matter) && medChronAsset
      ? [{ id: 'chronology', label: 'Medical Chronology', href: matterPaths.chronology.tab(matterId, MedChronAssetTabs.ALL_EVENTS), icon: MedChron }]
      : []),
    ...(canViewMedicalBills(matter) && medSummaryAsset
      ? [
          {
            id: 'billing-summary',
            label: 'Medical Bills',
            href: matterPaths.billing.assetTab(matterId, MedSummaryAssetTabs.ALL_ENTRIES),
            icon: MedicalBillsNew,
          },
        ]
      : []),
  ]

  const viewableCount = [canViewTimeline(matter), canViewMedChron(matter), canViewMedicalBills(matter)].filter(Boolean).length
  const showBuildButton = viewableCount > items.length

  return { items, showBuildButton }
}
